/** Supplier list table using TanStack Table with sorting and expandable gateways. */
import React, { useMemo, useState } from "react";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Collapse,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Router as GatewayIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  createColumnHelper,
  SortingState,
  ExpandedState,
  flexRender,
} from "@tanstack/react-table";
import { Supplier, SupplierStatus } from "../../types";

interface SupplierListProps {
  suppliers: Supplier[];
  onEdit: (supplier: Supplier) => void;
  onDelete: (supplierId: string) => void;
  onToggleStatus: (supplierId: string, currentStatus: string) => void;
  onManageGateways: (supplier: Supplier) => void;
  onRateDeck: (supplier: Supplier) => void;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Supplier>();

/** Status chip component. */
function StatusChip({ status }: { status: string }) {
  const colorMap: Record<string, "success" | "default" | "warning"> = {
    ACTIVE: "success",
    DISABLED: "default",
    SUSPENDED: "warning",
  };
  const color = colorMap[status] || "default";
  return <Chip label={status} size="small" color={color} />;
}

/** Gateway count chip. */
function GatewayCount({ count, enabled }: { count: number; enabled: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <GatewayIcon fontSize="small" color="action" />
      <Typography variant="body2">
        {enabled}/{count}
      </Typography>
    </Box>
  );
}

/** Supplier list table with sortable columns, expandable gateways, and row actions. */
export default function SupplierList({
  suppliers,
  onEdit,
  onDelete,
  onToggleStatus,
  onManageGateways,
  onRateDeck,
}: SupplierListProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  /** Format capacity value. */
  const formatCapacity = (value: number): string => {
    return value === 0 ? "Unlimited" : value.toLocaleString();
  };

  /** Table columns definition. */
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "expander",
        header: "",
        cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => row.toggleExpanded()}
            disabled={row.original.gateways.length === 0}
           aria-label="Expand less">
            {row.getIsExpanded() ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        ),
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      columnHelper.accessor("hostname", {
        header: "Hostname",
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      columnHelper.accessor("gateways", {
        header: "Gateways",
        cell: (info) => {
          const gateways = info.getValue();
          const enabled = gateways.filter((g) => g.enabled).length;
          return <GatewayCount count={gateways.length} enabled={enabled} />;
        },
        enableSorting: false,
      }),
      columnHelper.accessor("cgr_subject", {
        header: "CGR Subject",
        cell: (info) => info.getValue() || "-",
        enableSorting: false,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusChip status={info.getValue()} />,
        enableSorting: true,
      }),
      columnHelper.accessor("capacity_cps", {
        header: "Max CPS",
        cell: (info) => formatCapacity(info.getValue()),
        enableSorting: true,
      }),
      columnHelper.accessor("capacity_cc", {
        header: "Max CC",
        cell: (info) => formatCapacity(info.getValue()),
        enableSorting: true,
      }),
      columnHelper.accessor("priority", {
        header: "Priority",
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.id, {
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Tooltip title="Edit Supplier">
              <IconButton
                size="small"
                onClick={() => onEdit(suppliers[info.row.index])}
                color="primary"
               aria-label="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rate Deck">
              <IconButton
                size="small"
                onClick={() => onRateDeck(suppliers[info.row.index])}
                color="secondary"
               aria-label="Rate deck">
                <ListAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage Gateways">
              <IconButton
                size="small"
                onClick={() => onManageGateways(suppliers[info.row.index])}
                color="info"
               aria-label="Gateway">
                <GatewayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                suppliers[info.row.index].status === SupplierStatus.ACTIVE
                  ? "Disable Supplier"
                  : "Enable Supplier"
              }
            >
              <IconButton
                size="small"
                onClick={() =>
                  onToggleStatus(info.row.id, suppliers[info.row.index].status)
                }
                color={suppliers[info.row.index].status === SupplierStatus.ACTIVE ? "warning" : "success"}
               aria-label="Block">
                {suppliers[info.row.index].status === SupplierStatus.ACTIVE ? (
                  <BlockIcon fontSize="small" />
                ) : (
                  <CheckIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Supplier">
              <IconButton
                size="small"
                onClick={() => setDeleteConfirm(info.row.id)}
                color="error"
               aria-label="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        enableSorting: false,
      }),
    ],
    [suppliers, onEdit, onToggleStatus, onManageGateways, onRateDeck]
  );

  /** Table instance with sorting and expansion. */
  const table = useReactTable({
    data: suppliers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      expanded,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
  });

  /** Handle delete confirmation. */
  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDelete(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  if (suppliers.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No suppliers found. Click "Add Supplier" to create your first supplier.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sortDirection={
                      header.column.getIsSorted() ? (header.column.getIsSorted() as "asc" | "desc") : false
                    }
                    sx={{ fontWeight: "bold" }}
                  >
                    {header.column.getCanSort() ? (
                      <TableSortLabel
                        active={header.column.getIsSorted() !== false}
                        direction={header.column.getIsSorted() === "asc" ? "asc" : "desc"}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  hover
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() && row.original.gateways.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 0, bgcolor: "action.hover" }}>
                      <Collapse in={row.getIsExpanded()} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Gateways ({row.original.gateways.length})
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>IP Address</TableCell>
                                <TableCell>Port</TableCell>
                                <TableCell>Priority</TableCell>
                                <TableCell>Load Share</TableCell>
                                <TableCell>Health</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.original.gateways.map((gateway) => (
                                <TableRow key={gateway.id}>
                                  <TableCell>{gateway.ip_address}</TableCell>
                                  <TableCell>{gateway.port}</TableCell>
                                  <TableCell>{gateway.priority}</TableCell>
                                  <TableCell>{gateway.load_share}%</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={gateway.health_status}
                                      size="small"
                                      color={
                                        gateway.health_status === "healthy"
                                          ? "success"
                                          : gateway.health_status === "down"
                                          ? "error"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={gateway.enabled ? "Enabled" : "Disabled"}
                                      size="small"
                                      color={gateway.enabled ? "success" : "default"}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Confirm Delete Supplier</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this supplier? This will also delete all associated
            gateways and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
